import React, { useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import {
  getCompanyProfile,
  getProfileSectionDefinitions,
  useAddFileSource,
  useAddUrlSource,
  useCreateCompanyProfile,
  useDeleteProfileSource,
  useResetCompanyProfile,
  useRetrySourceProcessing,
  useSaveProfileSection,
  useUpdateSourceSharing,
} from '../../queries/companyProfile';
import { getMyCompany } from '../../queries/myCompany';
import CompanyProfileHeader from './CompanyProfileHeader';
import BuildWorkflowBanner from './BuildWorkflowBanner';
import SourcesColumn from './sources/SourcesColumn';
import { countReadySources, failedSources, isSourcePending, pendingSources } from './sources/sourceStatus';
import ProfileSectionsColumn from './sections/ProfileSectionsColumn';
import ProfileCardDrawer from './sections/ProfileCardDrawer';
import GtmDoctrineColumn from './gtm/GtmDoctrineColumn';
import ProcessingCompanyProfileModal from './ProcessingCompanyProfileModal';
import AddProgramRequiredModal from './AddProgramRequiredModal';
import useProfileProcessingModal from './useProfileProcessingModal';
import { NO_CHANGES_REASON, hasChangesSinceBuild } from './profileChanges';

const ClientCompanyProfilePage = () => {
  const [openCard, setOpenCard] = useState(null);

  const {
    data: profileData,
    isLoading: isProfileLoading,
    isError: isProfileError,
  } = useQuery({
    ...getCompanyProfile(),
    refetchInterval: (query) => {
      const list = query.state.data?.sources || [];
      const current = query.state.data?.profile;
      if (current?.status === 'generating') return 2500;
      return list.some(isSourcePending) ? 2500 : false;
    },
  });
  const { data: definitions = [], isLoading: isDefinitionsLoading } = useQuery(getProfileSectionDefinitions());
  const {
    data: myCompany,
    isPending: isCompanyPending,
    isError: isCompanyError,
    error: companyError,
  } = useQuery({
    ...getMyCompany({}),
    retry: false,
  });

  const { mutateAsync: createProfile, isPending: isCreating } = useCreateCompanyProfile();
  const { mutateAsync: saveSection, isPending: isSavingSection } = useSaveProfileSection();
  const { mutateAsync: addFileSource } = useAddFileSource();
  const { mutateAsync: addUrlSource, isPending: isAddingUrl } = useAddUrlSource();
  const { mutateAsync: deleteSource, isPending: isDeleting, variables: deleteVariables } = useDeleteProfileSource();
  const {
    mutateAsync: updateSharing,
    isPending: isUpdatingSharing,
    variables: sharingVariables,
  } = useUpdateSourceSharing();
  const { mutateAsync: retrySource, isPending: isRetrying, variables: retryVariables } = useRetrySourceProcessing();
  const { mutateAsync: resetProfile, isPending: isResetting } = useResetCompanyProfile();

  const profile = profileData?.profile || null;
  const sources = useMemo(() => profileData?.sources || [], [profileData?.sources]);
  const hasSections = (profile?.sections || []).length > 0;
  const isBuilt = profile?.status === 'ready' && hasSections;
  const isGenerating = profile?.status === 'generating';
  const mode = isBuilt ? 'built' : 'build';
  const processing = useProfileProcessingModal({
    profile,
    isGenerating,
    isCreating,
    createProfile,
  });

  const documents = useMemo(() => sources.filter((source) => source.type === 'file'), [sources]);
  const urls = useMemo(() => sources.filter((source) => source.type === 'url'), [sources]);
  const programs = myCompany?.products || [];
  const companyNotFound = isCompanyError && companyError?.response?.status === 404;
  const requiresProgram = !isCompanyPending && programs.length === 0 && (companyNotFound || myCompany != null);
  const hasSources = documents.length + urls.length > 0;

  // A source counts only once the full flow (text extraction, then knowledge)
  // has finished, so Build waits for the whole pipeline rather than the upload.
  const readyCount = useMemo(() => countReadySources(sources), [sources]);
  const pendingCount = useMemo(() => pendingSources(sources).length, [sources]);
  const failedCount = useMemo(() => failedSources(sources).length, [sources]);

  const handleUploadFile = async (file) => {
    await addFileSource(file);
  };

  const handleAddUrl = async (url) => {
    try {
      await addUrlSource({ url });
      toast.success('URL added');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to add URL');
    }
  };

  const handleRemoveSource = async (source) => {
    try {
      await deleteSource({ id: source._id });
      toast.success(source.type === 'file' ? 'Document removed' : 'URL removed');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to remove source');
    }
  };

  const handleRetrySource = async (source) => {
    try {
      await retrySource({ id: source._id });
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to retry this source');
    }
  };

  const handleToggleSharing = async (source) => {
    try {
      await updateSharing({
        id: source._id,
        sharedWithLeadAgents: !source.sharedWithLeadAgents,
      });
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to update sharing');
    }
  };

  useEffect(() => {
    if (isGenerating) setOpenCard(null);
  }, [isGenerating]);

  const handlePrimaryAction = async () => {
    if (!hasSources) {
      toast.error('Add at least one document or URL to continue.');
      return;
    }
    setOpenCard(null);
    await processing.start();
  };

  const handleReset = async () => {
    try {
      await resetProfile();
      setOpenCard(null);
      toast.success('Company Profile reset. You can start building again.');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to reset Company Profile');
      throw error;
    }
  };

  const openSection = openCard
    ? (profile?.sections || []).find((section) => {
        const sameCard = String(section.definitionId) === String(openCard.definition._id);
        const sameProgram = openCard.programId
          ? String(section.programId) === String(openCard.programId)
          : !section.programId;
        return sameCard && sameProgram;
      })
    : null;

  const persistSection = async (action, entries) => {
    if (!openCard) return;
    await saveSection({
      definitionId: openCard.definition._id,
      programId: openCard.programId,
      action,
      entries: entries?.map((entry) => ({
        id: entry.id,
        topic: entry.topic,
        text: entry.text,
      })),
    });
  };

  const resolvePrimaryBlockReason = () => {
    if (isGenerating) return '';
    if (pendingCount > 0) return 'Wait for your sources to finish processing.';
    if (!programs.length) return 'Add at least one Program before building your Company Profile.';
    if (!hasSources) return 'Add at least one document or URL to continue.';
    if (readyCount === 0) {
      return 'None of your sources could be processed. Retry one or add another to continue.';
    }
    if (isBuilt && !hasChangesSinceBuild(profile, sources, programs)) return NO_CHANGES_REASON;
    return '';
  };

  const primaryDisabledReason = resolvePrimaryBlockReason();
  const isPrimaryDisabled = !!primaryDisabledReason;

  if (isProfileLoading || isDefinitionsLoading || isCompanyPending) {
    return (
      <Box display='flex' justifyContent='center' alignItems='center' minHeight={360}>
        <CircularProgress />
      </Box>
    );
  }

  if (isProfileError) {
    return (
      <Box p={4}>
        <Typography color='error'>Failed to load Company Profile. Please try again.</Typography>
      </Box>
    );
  }

  return (
    <Box px={{ xs: 2.5, md: 4 }} py={3} bgcolor='#fff' minHeight='100%'>
      <CompanyProfileHeader
        mode={mode}
        showReset={Boolean(profile)}
        updatedAt={profile?.updatedAt}
        onReset={handleReset}
        isResetting={isResetting}
      />
      {mode === 'build' && <BuildWorkflowBanner />}

      <Box
        display='grid'
        gridTemplateColumns={{
          xs: '1fr',
          lg: 'minmax(280px, 1fr) minmax(300px, 1.15fr) minmax(260px, 0.9fr)',
        }}
        gap={2.5}
        alignItems='start'
      >
        <SourcesColumn
          mode={mode}
          sourcesLocked={isGenerating}
          documents={documents}
          urls={urls}
          onUploadFile={handleUploadFile}
          onAddUrl={handleAddUrl}
          onRemoveSource={handleRemoveSource}
          onToggleSharing={handleToggleSharing}
          onPrimaryAction={handlePrimaryAction}
          isAddingUrl={isAddingUrl}
          isPrimaryLoading={isCreating || isGenerating}
          removingId={isDeleting ? deleteVariables?.id : null}
          sharingId={isUpdatingSharing ? sharingVariables?.id : null}
          isPrimaryDisabled={isPrimaryDisabled}
          primaryDisabledReason={isGenerating ? 'Building your Company Profile.' : primaryDisabledReason}
          onRetrySource={handleRetrySource}
          retryingId={isRetrying ? retryVariables?.id : null}
          readyCount={readyCount}
          failedCount={failedCount}
        />
        <ProfileSectionsColumn
          mode={isGenerating ? 'generating' : mode}
          definitions={definitions}
          programs={programs}
          sections={profile?.sections || []}
          buildError={profile?.status === 'failed' ? profile.buildError : ''}
          onOpenCard={(definition, programId) => setOpenCard({ definition, programId })}
        />
        <GtmDoctrineColumn />
      </Box>
      <AddProgramRequiredModal open={requiresProgram} />
      <ProcessingCompanyProfileModal
        open={processing.open}
        generationStep={profile?.generationStep}
        allComplete={processing.allComplete}
        error={processing.error}
        isRetrying={processing.isRetrying}
        onRetry={processing.retry}
        onClose={processing.closeError}
      />
      <ProfileCardDrawer
        open={Boolean(openCard)}
        card={openCard?.definition}
        section={openSection}
        isSaving={isSavingSection}
        onClose={() => setOpenCard(null)}
        onReview={async () => {
          try {
            await persistSection('review');
          } catch (error) {
            toast.error(error?.response?.data?.message || 'Failed to mark this card reviewed');
          }
        }}
        onSave={async (entries) => {
          try {
            await persistSection('save', entries);
            setOpenCard(null);
            toast.success('Card saved');
          } catch (error) {
            toast.error(error?.response?.data?.message || 'Failed to save this card');
          }
        }}
      />
    </Box>
  );
};

export default ClientCompanyProfilePage;
